import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { apiRequest } from '../config/api';
import { useAuth } from './AuthContext';

interface NotificationContextValue {
  unreadCount: number;
  markRead: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue>({
  unreadCount: 0,
  markRead: async () => {},
});

export const useNotifications = () => useContext(NotificationContext);

const POLL_INTERVAL_MS = 60_000; // 1 minute

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchUnread = useCallback(async () => {
    if (!user) return;
    try {
      const res = await apiRequest('/notifications/unread');
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.count);
      }
    } catch {}
  }, [user]);

  const markRead = useCallback(async () => {
    try {
      await apiRequest('/notifications/mark-read', { method: 'POST' });
      setUnreadCount(0);
    } catch {}
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchUnread();
    intervalRef.current = setInterval(fetchUnread, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [user, fetchUnread]);

  return (
    <NotificationContext.Provider value={{ unreadCount, markRead }}>
      {children}
    </NotificationContext.Provider>
  );
};
